import { useMutation } from '@tanstack/react-query'
import { formatUsd, type AdminUser } from '@ytclip/shared'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { SwitchField, TextField } from '@/components/ui/field.tsx'
import { adjustUserBalance, blockUser } from '@/features/admin/api.ts'
import { ApiRequestError } from '@/lib/api-client.ts'

function describe(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback
}

export function BlockDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [notify, setNotify] = useState(false)

  const block = useMutation({
    mutationFn: () => blockUser(user?.id as string, { reason, notify }),
    onSuccess: () => {
      toast.success('Akun diblokir')
      setReason('')
      setNotify(false)
      onDone()
      onClose()
    },
    onError: (error) => toast.error(describe(error, 'Gagal memblokir akun')),
  })

  return (
    <Dialog
      open={Boolean(user)}
      onOpenChange={onClose}
      title="Blokir akun"
      description={`${user?.name ?? ''} tidak akan bisa mengakses dashboard. Berlaku dalam waktu satu menit.`}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Batal
          </Button>
          <Button variant="danger" loading={block.isPending} onClick={() => block.mutate()}>
            Blokir
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Alasan"
          placeholder="Penyalahgunaan layanan"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <SwitchField
          label="Kirim email pemberitahuan"
          description="Butuh SMTP yang sudah dikonfigurasi."
          checked={notify}
          onChange={setNotify}
        />
      </div>
    </Dialog>
  )
}

export function AdjustDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null
  onClose: () => void
  onDone: () => void
}) {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')

  const adjust = useMutation({
    mutationFn: () =>
      adjustUserBalance(user?.id as string, { usdAmount: Number(amount), note }),
    onSuccess: (result) => {
      toast.success(`Saldo sekarang ${formatUsd(result.balanceUsd)}`)
      setAmount('')
      setNote('')
      onDone()
      onClose()
    },
    onError: (error) => toast.error(describe(error, 'Gagal menyesuaikan saldo')),
  })

  const parsed = Number(amount)
  const valid = Number.isFinite(parsed) && parsed !== 0 && note.trim().length > 0

  return (
    <Dialog
      open={Boolean(user)}
      onOpenChange={onClose}
      title="Koreksi saldo"
      description={`Penyesuaian manual untuk ${user?.email ?? ''}. Tercatat di ledger.`}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Batal
          </Button>
          <Button disabled={!valid} loading={adjust.isPending} onClick={() => adjust.mutate()}>
            Terapkan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Jumlah (USD)"
          prefix="$"
          inputMode="decimal"
          placeholder="10 atau -10"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          hint="Nilai negatif menarik kembali kredit."
        />
        <TextField
          label="Catatan"
          placeholder="Kompensasi pembayaran yang tertahan"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Dialog>
  )
}
